import React, { useState, useEffect, useCallback, useRef } from 'react';
import StatusBadge from '../common/StatusBadge';
import { formatDate, formatDateTime, formatCurrency } from '../../utils/formatters';
import {
  fetchOrganizations,
  fetchOrganizationById,
  createOrganization,
  updateOrganization,
  addOrgSlp,
  addOrgAdmin,
  createContract,
  fetchSubscriptionPlans,
} from '../../api/adminClient';

/* ── Shared modal styles ─────────────────────────────────────────────────── */
const BACKDROP = { backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)' };
const CARD_STYLE = {
  borderRadius: '1.25rem',
  border: 'none',
  boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
  overflow: 'hidden',
};
const HEADER_STYLE = {
  background: 'linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)',
  borderBottom: '1px solid rgba(220,226,240,0.7)',
};

/* ── Org Avatar ──────────────────────────────────────────────────────────── */
const PALETTE = [
  ['rgba(58,111,247,0.12)', '#3a6ff7'],
  ['rgba(16,185,129,0.12)', '#10b981'],
  ['rgba(139,92,246,0.12)', '#8b5cf6'],
  ['rgba(234,88,12,0.12)', '#ea580c'],
  ['rgba(220,38,38,0.12)', '#dc2626'],
  ['rgba(217,70,239,0.12)', '#d946ef'],
  ['rgba(234,179,8,0.12)', '#ca8a04'],
];

const OrgAvatar = ({ name, size = 42 }) => {
  const [bg, fg] = PALETTE[(name || '').charCodeAt(0) % PALETTE.length];
  const initials = (name || '??')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
  return (
    <div
      style={{
        width: size, height: size,
        borderRadius: '12px',
        background: bg, color: fg,
        display: 'grid', placeItems: 'center',
        fontWeight: 700, fontSize: size * 0.36,
        border: `1.5px solid ${fg}33`,
        flexShrink: 0,
        letterSpacing: '-0.02em',
      }}
    >
      {initials}
    </div>
  );
};

/* ── Create / Edit Org Modal ─────────────────────────────────────────────── */
const EMPTY_ORG_FORM = {
  name: '',
  contact_person_email: '',
  contactPersonFirstName: '',
  contactPersonLastName: '',
  billing_email: '',
  contact_phone: '',
  address: '',
  status: 'active',
};

const OrgFormModal = ({ title, form, onField, onSubmit, onClose, submitting, error, showStatus, zIndex }) => (
  <div className="modal show d-block" tabIndex="-1" style={{ ...BACKDROP, ...(zIndex ? { zIndex } : {}) }}>
    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 540 }}>
      <div className="modal-content" style={CARD_STYLE}>
        <div className="modal-header border-0 px-4 pt-4 pb-3" style={HEADER_STYLE}>
          <h5 className="modal-title fw-bold mb-0">{title}</h5>
          <button type="button" className="btn-close" onClick={onClose} disabled={submitting} />
        </div>

        <form onSubmit={onSubmit}>
          <div className="modal-body px-4 pt-3 pb-2">
            {error && (
              <div className="alert alert-danger rounded-3 py-2 small mb-3">{error}</div>
            )}

            <div className="mb-3">
              <label className="form-label text-muted small fw-semibold mb-1">
                Organization Name <span className="text-danger">*</span>
              </label>
              <input
                className="form-control rounded-3"
                name="name"
                value={form.name}
                onChange={onField}
                placeholder="e.g. Acme Therapy Center"
                required
                autoFocus
              />
            </div>

            <div className="mb-3">
              <label className="form-label text-muted small fw-semibold mb-1">
                Contact Person Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className="form-control rounded-3"
                name="contact_person_email"
                value={form.contact_person_email}
                onChange={onField}
                placeholder="admin@example.com"
                required={!showStatus}
              />
              {!showStatus && (
                <small className="text-muted">A set-password invite will be sent to this address.</small>
              )}
            </div>

            <div className="row g-3 mb-3">
              <div className="col-6">
                <label className="form-label text-muted small fw-semibold mb-1">First Name</label>
                <input
                  className="form-control rounded-3"
                  name="contactPersonFirstName"
                  value={form.contactPersonFirstName}
                  onChange={onField}
                  placeholder="Jane"
                />
              </div>
              <div className="col-6">
                <label className="form-label text-muted small fw-semibold mb-1">Last Name</label>
                <input
                  className="form-control rounded-3"
                  name="contactPersonLastName"
                  value={form.contactPersonLastName}
                  onChange={onField}
                  placeholder="Smith"
                />
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-7">
                <label className="form-label text-muted small fw-semibold mb-1">Billing Email</label>
                <input
                  type="email"
                  className="form-control rounded-3"
                  name="billing_email"
                  value={form.billing_email}
                  onChange={onField}
                  placeholder="billing@example.com"
                />
              </div>
              <div className="col-5">
                <label className="form-label text-muted small fw-semibold mb-1">Phone</label>
                <input
                  className="form-control rounded-3"
                  name="contact_phone"
                  value={form.contact_phone}
                  onChange={onField}
                  placeholder="123-456-7890"
                />
              </div>
            </div>

            <div className={showStatus ? 'mb-3' : 'mb-1'}>
              <label className="form-label text-muted small fw-semibold mb-1">Address</label>
              <input
                className="form-control rounded-3"
                name="address"
                value={form.address}
                onChange={onField}
                placeholder="123 Main St, City, State"
              />
            </div>

            {showStatus && (
              <div className="mb-1">
                <label className="form-label text-muted small fw-semibold mb-1">Status</label>
                <select
                  className="form-select rounded-3"
                  name="status"
                  value={form.status}
                  onChange={onField}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            )}
          </div>

          <div className="modal-footer border-0 px-4 pb-4 pt-3 gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary rounded-pill px-4"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary rounded-pill px-4 fw-semibold shadow-sm"
              disabled={submitting}
            >
              {submitting ? (
                <><span className="spinner-border spinner-border-sm me-2" role="status" />Saving…</>
              ) : showStatus ? 'Save Changes' : 'Create Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
);

/* ── Contract Modal ──────────────────────────────────────────────────────── */
const ContractModal = ({ org, onClose, onSuccess, zIndex }) => {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [priceNotes, setPriceNotes] = useState('');
  const [docFile, setDocFile] = useState(null);
  /** Per-plan quantity (seats / hours allocation); string keys match plan uuid */
  const [planQuantities, setPlanQuantities] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchSubscriptionPlans()
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.data || [];
        setPlans(list);
        setPlanQuantities((prev) => {
          const next = { ...prev };
          for (const p of list) {
            if (!(p.uuid in next)) next[p.uuid] = '';
          }
          return next;
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const updatePlanQuantity = (planUuid, raw) => {
    if (raw === '') {
      setPlanQuantities((q) => ({ ...q, [planUuid]: '' }));
      return;
    }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 0) return;
    setPlanQuantities((q) => ({ ...q, [planUuid]: String(n) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      setError('Start and end dates are required.');
      return;
    }
    const licenses = plans
      .map((p) => ({
        planUuid: p.uuid,
        quantity: Math.max(0, parseInt(planQuantities[p.uuid] || '0', 10) || 0),
      }))
      .filter((l) => l.quantity >= 1);
    if (licenses.length === 0) {
      setError('Enter a quantity of at least 1 for at least one plan.');
      return;
    }
    setSubmitting(true);
    setError('');

    const fd = new FormData();
    fd.append('startDate', startDate);
    fd.append('endDate', endDate);
    if (priceNotes) fd.append('price_notes', priceNotes);
    if (docFile) fd.append('document', docFile);
    fd.append('licenses', JSON.stringify(licenses));

    try {
      await createContract(org.uuid ?? org.id, fd);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create contract.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ ...BACKDROP, ...(zIndex ? { zIndex } : {}) }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content" style={CARD_STYLE}>
          <div className="modal-header border-0 px-4 pt-4 pb-3" style={HEADER_STYLE}>
            <div>
              <h5 className="modal-title fw-bold mb-0">New Contract</h5>
              <small className="text-muted fw-medium">{org.name}</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose} disabled={submitting} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body px-4 pt-3 pb-2">
              {error && (
                <div className="alert alert-danger rounded-3 py-2 small mb-3">{error}</div>
              )}

              {/* Date range */}
              <div className="row g-3 mb-4">
                <div className="col-6">
                  <label className="form-label text-muted small fw-semibold mb-1">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control rounded-3"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>
                <div className="col-6">
                  <label className="form-label text-muted small fw-semibold mb-1">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control rounded-3"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* License allocation */}
              <div className="mb-4">
                <label className="form-label text-muted small fw-semibold mb-2">
                  License Allocation <span className="text-danger">*</span>
                </label>

                {plansLoading ? (
                  <div className="text-muted small py-2">Loading plans…</div>
                ) : plans.length === 0 ? (
                  <div className="alert alert-warning rounded-3 py-2 small">
                    No subscription plans available.
                  </div>
                ) : (
                  <div
                    className="rounded-3 overflow-hidden"
                    style={{ border: '1px solid rgba(220,226,240,0.8)' }}
                  >
                    {/* Column headers */}
                    <div
                      className="px-3 py-2"
                      style={{ background: '#f8faff', borderBottom: '1px solid rgba(220,226,240,0.8)' }}
                    >
                      <div className="row g-0">
                        <div className="col-8 small text-muted fw-semibold">Plan</div>
                        <div className="col-4 small text-muted fw-semibold text-center">
                          Qty <span className="fw-normal">(seats / hours)</span>
                        </div>
                      </div>
                    </div>

                    {plans.map((p, i) => (
                      <div
                        key={p.uuid}
                        className="px-3 py-2"
                        style={{
                          borderBottom:
                            i < plans.length - 1 ? '1px solid rgba(220,226,240,0.5)' : 'none',
                        }}
                      >
                        <div className="row g-2 align-items-center">
                          <div className="col-8">
                            <div className="fw-medium small">{p.plan_name}</div>
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {p.hoursGranted ? `${p.hoursGranted}h/mo · ` : ''}
                              {p.plan_price != null && p.plan_price !== '' ? `$${p.plan_price}` : ''}
                            </div>
                          </div>
                          <div className="col-4">
                            <input
                              type="number"
                              className="form-control form-control-sm rounded-3 text-center"
                              min="0"
                              step="1"
                              placeholder="0"
                              value={planQuantities[p.uuid] ?? ''}
                              onChange={(e) => updatePlanQuantity(p.uuid, e.target.value)}
                              aria-label={`Quantity for ${p.plan_name}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pricing notes */}
              <div className="mb-3">
                <label className="form-label text-muted small fw-semibold mb-1">Pricing Notes</label>
                <textarea
                  className="form-control rounded-3"
                  rows="2"
                  value={priceNotes}
                  onChange={(e) => setPriceNotes(e.target.value)}
                  placeholder="e.g. Annual contract, invoiced quarterly…"
                />
              </div>

              {/* Document upload */}
              <div className="mb-1">
                <label className="form-label text-muted small fw-semibold mb-1">
                  Contract Document
                </label>
                <input
                  type="file"
                  className="form-control rounded-3"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => setDocFile(e.target.files[0] || null)}
                />
                <small className="text-muted">PDF or image. Optional.</small>
              </div>
            </div>

            <div className="modal-footer border-0 px-4 pb-4 pt-3 gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill px-4"
                onClick={onClose}
                disabled={submitting}
              >
                Skip for now
              </button>
              <button
                type="submit"
                className="btn btn-primary rounded-pill px-4 fw-semibold shadow-sm"
                disabled={submitting || plansLoading}
              >
                {submitting ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status" />Creating…</>
                ) : 'Create Contract'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

/* ── Add Member Modal ────────────────────────────────────────────────────── */
const AddMemberModal = ({ orgId, onClose, onSuccess, zIndex }) => {
  const [role, setRole] = useState('slp');
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', username: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { setError('Email is required.'); return; }
    setSubmitting(true);
    setError('');
    try {
      if (role === 'slp') {
        await addOrgSlp(orgId, form);
      } else {
        await addOrgAdmin(orgId, form);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add member.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ ...BACKDROP, ...(zIndex ? { zIndex } : {}) }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 460 }}>
        <div className="modal-content" style={CARD_STYLE}>
          <div className="modal-header border-0 px-4 pt-4 pb-3" style={HEADER_STYLE}>
            <h5 className="modal-title fw-bold mb-0">Add Member</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={submitting} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body px-4 pt-3 pb-2">
              {error && (
                <div className="alert alert-danger rounded-3 py-2 small mb-3">{error}</div>
              )}

              <div className="mb-3">
                <label className="form-label text-muted small fw-semibold mb-2">Role</label>
                <div className="d-flex gap-2">
                  {[{ val: 'slp', label: 'SLP' }, { val: 'org-admin', label: 'Org Admin' }].map(({ val, label }) => (
                    <button
                      key={val}
                      type="button"
                      className={`btn btn-sm rounded-pill flex-grow-1 fw-semibold ${role === val ? 'btn-primary shadow-sm' : 'btn-outline-secondary'}`}
                      onClick={() => setRole(val)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label text-muted small fw-semibold mb-1">
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  className="form-control rounded-3"
                  name="email"
                  value={form.email}
                  onChange={handleField}
                  placeholder="jane@example.com"
                  required
                  autoFocus
                />
                <small className="text-muted">A set-password invite will be sent to this address.</small>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-6">
                  <label className="form-label text-muted small fw-semibold mb-1">First Name</label>
                  <input className="form-control rounded-3" name="firstName" value={form.firstName} onChange={handleField} placeholder="Jane" />
                </div>
                <div className="col-6">
                  <label className="form-label text-muted small fw-semibold mb-1">Last Name</label>
                  <input className="form-control rounded-3" name="lastName" value={form.lastName} onChange={handleField} placeholder="Smith" />
                </div>
              </div>

              <div className="mb-1">
                <label className="form-label text-muted small fw-semibold mb-1">Username</label>
                <input
                  className="form-control rounded-3"
                  name="username"
                  value={form.username}
                  onChange={handleField}
                  placeholder="jane.smith"
                />
              </div>
            </div>

            <div className="modal-footer border-0 px-4 pb-4 pt-3 gap-2">
              <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary rounded-pill px-4 fw-semibold shadow-sm" disabled={submitting}>
                {submitting
                  ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Adding…</>
                  : 'Add Member'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

/* ── Org Detail Modal ────────────────────────────────────────────────────── */
const OrgDetailModal = ({ org, data, loading, tab, onTab, onClose, onEdit, onContractCreated, onMemberAdded }) => {
  const [showAddMember, setShowAddMember] = useState(false);
  const [showContract, setShowContract] = useState(false);
  const hasChildModalOpen = showAddMember || showContract;

  const users = data?.users || [];
  const contracts = data?.contracts || [];
  const txns = data?.transactions || [];
  const activeContract = contracts.find((c) => c.status === 'active');
  const totalRevenue = txns.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const licenseUsage = data?.licenseUsage;
  const licensesByPlan = Array.isArray(licenseUsage?.byPlan) ? licenseUsage.byPlan : [];
  const licenseTotals =
    licenseUsage != null
      ? {
          granted: Number(licenseUsage.granted) || 0,
          used: Number(licenseUsage.used) || 0,
        }
      : null;

  return (
    <>
      {showAddMember && (
        <AddMemberModal
          orgId={org.id}
          zIndex={1080}
          onClose={() => setShowAddMember(false)}
          onSuccess={() => { onMemberAdded(); setShowAddMember(false); }}
        />
      )}
      {showContract && (
        <ContractModal
          org={org}
          zIndex={1080}
          onClose={() => setShowContract(false)}
          onSuccess={() => { onContractCreated(); setShowContract(false); }}
        />
      )}

      <div
        className="modal show d-block"
        tabIndex="-1"
        aria-hidden={hasChildModalOpen}
        style={{ ...BACKDROP, zIndex: hasChildModalOpen ? 1040 : 1050 }}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content" style={CARD_STYLE}>

            {/* ── Header ── */}
            <div className="modal-header border-0 px-4 pt-4 pb-3" style={HEADER_STYLE}>
              <div className="d-flex align-items-center gap-3 flex-grow-1" style={{ minWidth: 0 }}>
                <OrgAvatar name={org.name} size={52} />
                <div style={{ minWidth: 0 }}>
                  <h5 className="modal-title fw-bold mb-0 text-truncate">{org.name}</h5>
                  <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                    <StatusBadge status={org.status} />
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <button className="btn btn-outline-primary rounded-pill btn-sm px-3" onClick={onEdit}>
                  Edit
                </button>
                <button type="button" className="btn-close" onClick={onClose} />
              </div>
            </div>

            {/* ── Tabs ── */}
            <div className="border-bottom px-4" style={{ background: '#fafbff' }}>
              <div className="d-flex">
                {[
                  { key: 'overview', label: 'Overview' },
                  { key: 'contracts', label: `Contracts${!loading && contracts.length ? ` (${contracts.length})` : ''}` },
                  { key: 'members', label: `Members${!loading && users.length ? ` (${users.length})` : ''}` },
                  { key: 'licenses', label: 'Licenses' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    className="btn btn-link text-decoration-none px-3 py-3 fw-semibold"
                    style={{
                      color: tab === key ? '#3a6ff7' : '#6c757d',
                      borderBottom: tab === key ? '2px solid #3a6ff7' : '2px solid transparent',
                      borderRadius: 0,
                      fontSize: '0.88rem',
                    }}
                    onClick={() => onTab(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Tab content ── */}
            <div className="modal-body p-4" style={{ minHeight: 340, maxHeight: '62vh', overflowY: 'auto' }}>
              {loading ? (
                <div className="d-flex align-items-center justify-content-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading…</span>
                  </div>
                </div>
              ) : !data ? (
                <div className="text-center py-5 text-muted">
                  Could not load organization details.
                </div>
              ) : (
                <>
                  {/* Overview */}
                  {tab === 'overview' && (
                    <div>
                      <div className="row g-3 mb-4">
                        {[
                          {
                            label: 'Members', value: users.length,
                            color: '#3a6ff7', bg: 'rgba(58,111,247,0.07)', border: 'rgba(58,111,247,0.15)',
                          },
                          {
                            label: 'Contracts', value: contracts.length,
                            color: '#10b981', bg: 'rgba(16,185,129,0.07)', border: 'rgba(16,185,129,0.15)',
                          },
                          {
                            label: 'Active Contract',
                            value: activeContract
                              ? `${formatDate(activeContract.start_date)} – ${formatDate(activeContract.end_date)}`
                              : 'None',
                            color: activeContract ? '#8b5cf6' : '#9ca3af',
                            bg: activeContract ? 'rgba(139,92,246,0.07)' : 'rgba(156,163,175,0.07)',
                            border: activeContract ? 'rgba(139,92,246,0.15)' : 'rgba(156,163,175,0.15)',
                            small: true,
                          },
                          {
                            label: 'Total Revenue', value: formatCurrency(totalRevenue),
                            color: '#ea580c', bg: 'rgba(234,88,12,0.07)', border: 'rgba(234,88,12,0.15)',
                          },
                        ].map(({ label, value, color, bg, border, small }) => (
                          <div key={label} className="col-6 col-md-3">
                            <div className="p-3 rounded-3 h-100" style={{ background: bg, border: `1px solid ${border}` }}>
                              <div className="small fw-semibold mb-1" style={{ color: '#6c757d' }}>{label}</div>
                              <div
                                className="fw-bold"
                                style={{ color, fontSize: small ? '0.88rem' : '1.5rem', lineHeight: 1.2 }}
                              >
                                {value}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="row g-3">
                        {[
                          { label: 'Contact Person', value: data.contact_person_email || '—' },
                          { label: 'Billing Email', value: data.billing_email || '—' },
                          { label: 'Phone', value: data.contact_phone || '—' },
                          { label: 'Address', value: data.address || '—' },
                        ].map(({ label, value }) => (
                          <div key={label} className="col-12 col-md-6">
                            <div
                              className="p-3 rounded-3"
                              style={{ background: '#f8faff', border: '1px solid rgba(220,226,240,0.7)' }}
                            >
                              <div
                                className="small fw-semibold mb-1"
                                style={{ color: '#6c757d', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.06em' }}
                              >
                                {label}
                              </div>
                              <div className="fw-medium">{value}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Contracts */}
                  {tab === 'contracts' && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span
                          className="text-muted small fw-semibold text-uppercase"
                          style={{ letterSpacing: '0.06em' }}
                        >
                          {contracts.length} contract{contracts.length !== 1 ? 's' : ''}
                        </span>
                        <button
                          className="btn btn-sm btn-primary rounded-pill shadow-sm px-3 fw-semibold"
                          onClick={() => setShowContract(true)}
                        >
                          {activeContract ? '↻ Renew Contract' : '+ New Contract'}
                        </button>
                      </div>

                      {contracts.length ? (
                        <div className="d-flex flex-column gap-3">
                          {contracts.map((c) => (
                            <div
                              key={c.uuid}
                              className="p-3 rounded-3"
                              style={{
                                background: c.status === 'active' ? 'rgba(58,111,247,0.04)' : '#f8faff',
                                border: c.status === 'active'
                                  ? '1.5px solid rgba(58,111,247,0.2)'
                                  : '1px solid rgba(220,226,240,0.7)',
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-start gap-2">
                                <div>
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <StatusBadge status={c.status} />
                                    <span className="small fw-semibold">
                                      {formatDate(c.start_date)} – {formatDate(c.end_date)}
                                    </span>
                                  </div>
                                  {c.price_notes && (
                                    <small className="text-muted">{c.price_notes}</small>
                                  )}
                                </div>
                                {c.totalSeats != null && (
                                  <span className="badge rounded-pill bg-primary-subtle text-primary fw-semibold shadow-sm flex-shrink-0">
                                    {c.totalSeats} seat{c.totalSeats !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>

                              {c.licensesByPlan && c.licensesByPlan.length > 0 && (
                                <div className="mt-2 d-flex flex-wrap gap-2">
                                  {c.licensesByPlan.map((lp) => (
                                    <span
                                      key={lp.planName}
                                      className="badge rounded-pill fw-normal shadow-sm"
                                      style={{
                                        background: 'rgba(139,92,246,0.1)',
                                        color: '#8b5cf6',
                                        border: '1px solid rgba(139,92,246,0.2)',
                                      }}
                                    >
                                      {lp.planName}: {lp.assigned}/{lp.total} assigned
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <p className="text-muted mb-3">No contracts yet.</p>
                          <button
                            className="btn btn-primary rounded-pill shadow-sm px-4 fw-semibold"
                            onClick={() => setShowContract(true)}
                          >
                            + Create First Contract
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Members */}
                  {tab === 'members' && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="text-muted small fw-semibold text-uppercase" style={{ letterSpacing: '0.06em' }}>
                          {users.length} member{users.length !== 1 ? 's' : ''}
                        </span>
                        <button
                          className="btn btn-sm btn-primary rounded-pill shadow-sm px-3 fw-semibold"
                          onClick={() => setShowAddMember(true)}
                        >
                          + Add Member
                        </button>
                      </div>

                      {users.length ? (
                        <div className="table-responsive">
                          <table className="table align-middle mb-0">
                            <thead>
                              <tr>
                                <th className="text-muted fw-semibold">Member</th>
                                <th className="text-muted fw-semibold">Role</th>
                                <th className="text-muted fw-semibold">Status</th>
                                <th className="text-muted fw-semibold">Joined</th>
                              </tr>
                            </thead>
                            <tbody>
                              {users.map((u) => {
                                const roleLabel =
                                  u.user_type === 'o-slp' ? 'SLP'
                                  : u.user_type === 'org-admin' ? 'Org Admin'
                                  : u.user_type === 'org-full-admin' ? 'Full Admin'
                                  : u.user_type || '—';
                                return (
                                  <tr key={u.id || u.uuid}>
                                    <td>
                                      <div className="d-flex align-items-center gap-2">
                                        <OrgAvatar name={u.profile?.name || u.emailAddress || 'U'} size={32} />
                                        <div>
                                          <div className="fw-medium small">{u.profile?.name || '—'}</div>
                                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            {u.emailAddress || u.email || '—'}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <span className="badge rounded-pill bg-primary-subtle text-primary shadow-sm fw-semibold">
                                        {roleLabel}
                                      </span>
                                    </td>
                                    <td>
                                      <StatusBadge status={u.currentStatus || u.status} />
                                    </td>
                                    <td>
                                      <small className="text-muted">
                                        {u.createdAt ? formatDate(u.createdAt) : '—'}
                                      </small>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-5 text-muted">No members yet.</div>
                      )}
                    </div>
                  )}

                  {/* Licenses */}
                  {tab === 'licenses' && (
                    <div>
                      {!licenseUsage ? (
                        <div className="text-center py-5 text-muted">No license data available.</div>
                      ) : (
                        <>
                          <div className="row g-3 mb-4">
                            <div className="col-6 col-md-4">
                              <div
                                className="p-3 rounded-3 h-100"
                                style={{
                                  background: 'rgba(8,145,178,0.07)',
                                  border: '1px solid rgba(8,145,178,0.15)',
                                }}
                              >
                                <div className="small fw-semibold mb-1" style={{ color: '#6c757d' }}>Total granted</div>
                                <div className="fw-bold" style={{ color: '#0891b2', fontSize: '1.5rem', lineHeight: 1.2 }}>
                                  {licenseTotals.granted}
                                </div>
                              </div>
                            </div>
                            <div className="col-6 col-md-4">
                              <div
                                className="p-3 rounded-3 h-100"
                                style={{
                                  background: 'rgba(58,111,247,0.07)',
                                  border: '1px solid rgba(58,111,247,0.15)',
                                }}
                              >
                                <div className="small fw-semibold mb-1" style={{ color: '#6c757d' }}>Total used</div>
                                <div className="fw-bold" style={{ color: '#3a6ff7', fontSize: '1.5rem', lineHeight: 1.2 }}>
                                  {licenseTotals.used}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4 d-flex align-items-center">
                              <div className="text-muted small">
                                <span className="fw-semibold text-body">{licenseTotals.used}</span>
                                {' used of '}
                                <span className="fw-semibold text-body">{licenseTotals.granted}</span>
                                {' license'}
                                {licenseTotals.granted !== 1 ? 's' : ''}
                                {' organization-wide.'}
                              </div>
                            </div>
                          </div>

                          <h6
                            className="fw-semibold mb-3"
                            style={{ color: '#6c757d', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.08em' }}
                          >
                            By plan
                          </h6>
                          {licensesByPlan.length ? (
                            <div className="table-responsive">
                              <table className="table align-middle mb-0">
                                <thead>
                                  <tr>
                                    <th className="text-muted fw-semibold">Plan</th>
                                    <th className="text-muted fw-semibold">Granted</th>
                                    <th className="text-muted fw-semibold">Used</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {licensesByPlan.map((row) => (
                                    <tr key={row.planName || JSON.stringify(row)}>
                                      <td className="fw-medium">{row.planName || '—'}</td>
                                      <td>{Number(row.granted) || 0}</td>
                                      <td>{Number(row.used) || 0}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-muted small mb-0">No per-plan breakdown.</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer border-0 px-4 pb-4 pt-2">
              <button className="btn btn-outline-secondary rounded-pill px-4" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ── Main Component ──────────────────────────────────────────────────────── */
const OrganizationManagement = () => {
  const [orgs, setOrgs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounce = useRef(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [detailOrg, setDetailOrg] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState('overview');

  // After org creation, auto-open ContractModal for the new org
  const [pendingContractOrg, setPendingContractOrg] = useState(null);

  const [form, setForm] = useState(EMPTY_ORG_FORM);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(searchDebounce.current);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedSearch) {
        params.name = debouncedSearch;
        params.email = debouncedSearch;
      }
      const res = await fetchOrganizations(params);
      setOrgs(res.data?.organizations || []);
      setPagination(res.data?.pagination || null);
    } catch {
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (org) => {
    setDetailOrg(org);
    setDetailTab('overview');
    setDetailData(null);
    setDetailLoading(true);
    try {
      const res = await fetchOrganizationById(org.uuid);
      setDetailData(res.data?.data || res.data);
    } catch {
      setDetailData(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openCreate = () => {
    setForm(EMPTY_ORG_FORM);
    setFormError('');
    setShowCreate(true);
  };

  const openEdit = (org) => {
    setForm({
      name: org.name || '',
      contact_person_email: org.contact_person_email || '',
      contactPersonFirstName: org.contactPersonFirstName || '',
      contactPersonLastName: org.contactPersonLastName || '',
      billing_email: org.billing_email || '',
      contact_phone: org.contact_phone || '',
      address: org.address || '',
      status: org.status || 'active',
    });
    setFormError('');
    setEditOrg(org);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact_person_email.trim()) {
      setFormError('Organization name and contact person email are required.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k !== 'status' && v) fd.append(k, v);
      });
      const res = await createOrganization(fd);
      const newOrg = res.data?.data;
      setShowCreate(false);
      await load();
      // Automatically prompt to create a contract for the new org
      if (newOrg) {
        setPendingContractOrg({ id: newOrg.uuid, name: newOrg.name, uuid: newOrg.uuid });
      }
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to create organization.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError('Organization name is required.'); return; }
    setSubmitting(true);
    setFormError('');
    try {
      await updateOrganization(editOrg.uuid, {
        name: form.name,
        contact_person_email: form.contact_person_email,
        billing_email: form.billing_email,
        contact_phone: form.contact_phone,
        address: form.address,
        status: form.status,
      });
      setEditOrg(null);
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to update organization.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const totalOrgs = pagination?.total ?? orgs.length;
  const currentPage = pagination?.page ?? page;
  const totalPages = pagination?.pages ?? 1;

  return (
    <>
      {showCreate && (
        <OrgFormModal
          title="New Organization"
          form={form}
          onField={handleField}
          onSubmit={handleCreate}
          onClose={() => setShowCreate(false)}
          submitting={submitting}
          error={formError}
          showStatus={false}
        />
      )}

      {editOrg && (
        <OrgFormModal
          title="Edit Organization"
          form={form}
          onField={handleField}
          onSubmit={handleEdit}
          onClose={() => setEditOrg(null)}
          submitting={submitting}
          error={formError}
          showStatus
        />
      )}

      {/* Auto-opened after org creation — prompts user to add a contract */}
      {pendingContractOrg && (
        <ContractModal
          org={pendingContractOrg}
          onClose={() => setPendingContractOrg(null)}
          onSuccess={() => { load(); setPendingContractOrg(null); }}
        />
      )}

      {detailOrg && (
        <OrgDetailModal
          org={detailOrg}
          data={detailData}
          loading={detailLoading}
          tab={detailTab}
          onTab={setDetailTab}
          onClose={() => setDetailOrg(null)}
          onEdit={() => { setDetailOrg(null); openEdit(detailOrg); }}
          onContractCreated={() => loadDetail(detailOrg)}
          onMemberAdded={() => loadDetail(detailOrg)}
        />
      )}

      <div className="organizations-view">
        {/* ── Page header ── */}
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
          <div>
            <h1 className="display-6 fw-semibold mb-1">Organizations</h1>
            <p className="text-muted mb-0">
              Manage organizations, contracts, and their members.
            </p>
          </div>
          <button
            className="btn btn-primary rounded-pill shadow-sm px-4 fw-semibold"
            onClick={openCreate}
          >
            + New Organization
          </button>
        </div>

        {/* ── Filters ── */}
        <div className="card-soft mb-4 p-3">
          <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center">
            <input
              className="form-control rounded-pill shadow-sm flex-grow-1"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="form-select rounded-pill shadow-sm"
              style={{ maxWidth: 180 }}
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              disabled={loading}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="card-soft">
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th className="text-muted fw-semibold" style={{ minWidth: 260 }}>Organization</th>
                  <th className="text-muted fw-semibold">Status</th>
                  <th className="text-muted fw-semibold">Members</th>
                  <th className="text-muted fw-semibold">Contact Email</th>
                  <th className="text-muted fw-semibold">Phone</th>
                  <th className="text-muted fw-semibold">Created</th>
                  <th className="text-muted fw-semibold text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      Loading organizations…
                    </td>
                  </tr>
                ) : orgs.length ? (
                  orgs.map((org) => (
                    <tr key={org.id || org.uuid}>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <OrgAvatar name={org.name} />
                          <div style={{ minWidth: 0 }}>
                            <div className="fw-semibold text-truncate" style={{ maxWidth: 200 }}>
                              {org.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td><StatusBadge status={org.status} /></td>
                      <td>
                        <span className="badge rounded-pill bg-primary-subtle text-primary shadow-sm fw-semibold">
                          {org.users?.length ?? 0}
                        </span>
                      </td>
                      <td className="text-muted small">
                        {org.contact_person_email || org.billing_email || '—'}
                      </td>
                      <td className="text-muted small">{org.contact_phone || '—'}</td>
                      <td>
                        <small className="text-muted">
                          {org.createdAt ? formatDate(org.createdAt) : '—'}
                        </small>
                      </td>
                      <td className="text-end">
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            className="btn btn-sm btn-outline-primary rounded-pill shadow-sm"
                            onClick={() => loadDetail(org)}
                          >
                            View
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary rounded-pill shadow-sm"
                            onClick={() => openEdit(org)}
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      {debouncedSearch || statusFilter !== 'all'
                        ? 'No organizations match your filters.'
                        : 'No organizations yet. Create one to get started.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {pagination && totalPages > 1 && (
            <div className="card-footer bg-transparent border-0 px-4 py-3">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                <small className="text-muted">
                  Showing {((currentPage - 1) * 10) + 1}–{Math.min(currentPage * 10, totalOrgs)} of {totalOrgs}
                </small>
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-sm btn-outline-primary rounded-pill"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={loading || currentPage <= 1}
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      className={`btn btn-sm rounded-pill ${currentPage === p ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setPage(p)}
                      disabled={loading || currentPage === p}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    className="btn btn-sm btn-outline-primary rounded-pill"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={loading || currentPage >= totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OrganizationManagement;
